import { action, makeObservable, observable } from "mobx"

import { sendGTagEvent } from "@src/app/components/GTagEvent"
import { authApi } from "@src/shared/api"
import {
  SignupDetailsRequest,
  SignupDetailsRequestUserTypeEnum,
  SignupDetailsRequestUtms,
  SignupRequest,
} from "@src/shared/api/generated"
import { loading } from "@src/shared/classes/utils/loading"
import { getCookie } from "@src/shared/getCookie"
import { CookiesService } from "@src/shared/hooks/useCookies"

const referralLinkIdFromCookie = getCookie("referral_link_id")

export type RegistrationAuthRequest = {
  confirm_password: string
  details?: SignupDetailsRequest
  email: string
  fbPixel: {
    fb_click?: string
    fb_pixel?: string
  }
  password: string
  tos: boolean
  user_type: SignupDetailsRequestUserTypeEnum
  utms?: SignupDetailsRequestUtms
}

export type RegistrationSaveSelfInfoRequest = {
  company_name: string
  company_website?: string
  contacts?: Record<string, string>[]
  country_code_alpha_3?: string
  name: string
}

type State = {
  forceUserType?: SignupDetailsRequestUserTypeEnum
  identity: RegistrationAuthRequest
  referral_link_id: null | string
}

export class RegistrationStore extends loading([
  "registration",
  "getConfig",
  "saveSelfInfo",
]) {
  state: State = {
    forceUserType: undefined,
    identity: {} as RegistrationAuthRequest,
    referral_link_id: new URLSearchParams(window.location.search).get("id"),
  }

  constructor() {
    super()

    makeObservable(this, {
      createRequest: action.bound,
      registration: action.bound,
      setForceUserType: action.bound,
      setIdentity: action.bound,
      state: observable.deep,
    })
  }

  private handleRegistrationError(e: any, isFirstStep: boolean) {
    const TWO_STEPS_TARGETS = ["details"]

    const targets = e.response?.data?.error?.errors

    const firstStepIsValid =
      targets?.length &&
      targets.every((t: Record<string, string>) => {
        return TWO_STEPS_TARGETS.includes(t.target as string)
      })

    if (isFirstStep && firstStepIsValid) {
      return true
    }

    return Promise.reject(e)
  }

  createRequest(reCaptcha: string): SignupRequest {
    const { identity } = this.state
    const [fbc, fbp] = [CookiesService.get("_fbc"), CookiesService.get("_fbp")]
    const fb_pixel =
      fbc || fbp
        ? {
            ...(fbc ? { fb_click: fbc } : {}),
            ...(fbp ? { fb_pixel: fbp } : {}),
          }
        : null

    const userType =
      this.state.forceUserType ??
      (identity.user_type || SignupDetailsRequestUserTypeEnum.Merchant)

    return {
      // @TODO Delete the confirm_password field after removing on the server
      confirm_password: identity.password,
      email: identity.email,
      password: identity.password,
      re_captcha: reCaptcha,
      referral_link_id:
        this.state.referral_link_id ?? referralLinkIdFromCookie ?? undefined,
      tos: identity.tos,
      ...(identity.details
        ? {
            details: {
              ...identity.details,
              user_type: userType,
              ...(identity.utms ? { utms: identity.utms } : {}),
              ...(fb_pixel ? { fb_pixel } : {}),
            },
          }
        : {}),
    } as SignupRequest
  }

  async registration(
    identity: Partial<RegistrationAuthRequest>,
    reCaptcha: string,
  ) {
    this.setIdentity(identity)

    return authApi
      .signup(this.createRequest(reCaptcha))
      .then(({ data }) => {
        if (identity.details) {
          sendGTagEvent("registration")
        }

        return data
      })
      .catch((e) => this.handleRegistrationError(e, !identity.details))
  }

  setForceUserType(type: SignupDetailsRequestUserTypeEnum) {
    this.state.forceUserType = type

    this.state.identity = {
      ...this.state.identity,
      user_type: type,
    }
  }

  setIdentity(identity: Partial<RegistrationAuthRequest>) {
    this.state.identity = {
      ...this.state.identity,
      ...identity,
    }
  }
}
